import { TextCodes } from '@api/types/translation/TextCodes';
import { GalleryContext, Image } from '@components';
import { ArrowBack, ArrowForward, Close } from '@mui/icons-material';
import { Box, Modal, styled } from '@mui/material';
import { t } from '@translations';
import { useContext } from 'react';

const ImageGalleryNavigation = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const GalleryModalWrapper = styled(Modal)`
  &,
  * {
    outline: none !important;
  }
`;

const GalleryModalContent = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 80vh;
  gap: 16px;
  padding: 16px;
  margin: 16px;
  position: relative;
`;

const GalleryModalImage = styled(Box)`
  width: 100%;
  height: 70vh;
  img {
    width: 100%;
    height: 70vh;
    object-fit: scale-down;
  }
`;

const ImageGalleryModalArrow = styled(Box)`
  width: 36px;
  cursor: pointer;
`;

const GalleryCloseButton = styled(Box)`
  position: absolute;
  right: 16px;
  top: 16px;
  cursor: pointer;
`;

export const GalleryModal = () => {
  const {
    images = [],
    currentImage,
    setCurrentImage,
    isModalOpen,
    setIsModalOpen,
  } = useContext(GalleryContext);
  const imageCount = images.length;
  const hasPreviousImage = currentImage !== 0;
  const hasNextImage = imageCount - 1 > currentImage;

  const currentImageBigUrl = images[currentImage] ? images[currentImage]?.contentLink : '';
  const pictureSources = [
    { media: '(max-width:600px)', params: { width: 600, quality: 70, format: 'webp' } } as const,
    { media: '(max-width:1200px)', params: { width: 900, quality: 80, format: 'webp' } } as const,
    { params: { width: 1200, quality: 90, format: 'webp' } } as const,
  ];

  const sizes = '(max-width:600px) 100vw, (max-width:1200px) 80vw, 1200px';
  return (
    <GalleryModalWrapper
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      sx={{ outline: 'none' }}
    >
      <GalleryModalContent sx={{ p: 4, m: 4, background: '#fff' }}>
        <GalleryCloseButton>
          <Close onClick={() => setIsModalOpen(false)} />
        </GalleryCloseButton>
        <GalleryModalImage>
          <Image
            image={currentImageBigUrl}
            width='1200'
            height='800'
            pictureSources={pictureSources}
            sizes={sizes}
          />
        </GalleryModalImage>

        <ImageGalleryNavigation>
          <ImageGalleryModalArrow>
            {hasPreviousImage && (
              <ArrowBack onClick={() => setCurrentImage(Math.max(0, currentImage - 1))} />
            )}
          </ImageGalleryModalArrow>
          <Box>
            {t(TextCodes.Product.Details.CurrentImageCaption, {
              currentImage: (currentImage + 1).toString(),
              imageCount: imageCount.toString(),
            })}
          </Box>
          <ImageGalleryModalArrow>
            {hasNextImage && (
              <ArrowForward
                onClick={() => setCurrentImage(Math.min(images.length - 1, currentImage + 1))}
              />
            )}
          </ImageGalleryModalArrow>
        </ImageGalleryNavigation>
      </GalleryModalContent>
    </GalleryModalWrapper>
  );
};
